import React, { useEffect, useMemo, useState } from "react";
import { useLive } from "@/lib/live";
import ItemPicker from "@/components/ItemPicker";
import { AccountsHealthCard } from "@/components/AccountsHealthCard";
import { orderPickerItems } from "@/components/SheetBilling";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Wallet, Bank, ArrowsLeftRight, WarningCircle, ArrowClockwise, Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import SheetPurchasePanel, { SheetReturnsList } from "@/components/SheetPurchasePanel";
import { istToday } from "@/lib/istDate";

/**
 * AccountsPage — Owner-friendly Accounts Phase 1 UI.
 * Tabs: Dashboard | Money In | Money Out | Cash & Bank | Transfers |
 *       Categories | Reversals / Audit
 */
const OWNER_PURPOSES = [
  "Dad Education", "Mother Expense", "Brother Education", "Sister Education",
  "Household Expense", "Family Expense", "Personal Expense",
  "Travel", "Medical", "Pocket Money", "Other",
];

// Generic transfer instrument used only as metadata on payment-out rows.
// This is NOT a bank account — balances are always tracked in Financial
// Accounts. Owner is expected to first pick "Paid From Account" and
// then optionally tell us HOW the money moved.
const PAYMENT_METHODS = [
  "Cash", "UPI", "Bank Transfer", "NEFT / RTGS", "Cheque", "Card", "Other",
];

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "money_in",  label: "Money In" },
  { key: "money_out", label: "Money Out" },
  { key: "cash_bank", label: "Cash & Bank" },
  { key: "transfers", label: "Transfers" },
  { key: "suppliers", label: "Suppliers" },
  { key: "purchases", label: "Purchases" },
  { key: "returns",   label: "Purchase Returns" },
  { key: "categories", label: "Expense Categories" },
  { key: "reversals",  label: "Reversals / Audit" },
];

const CLASS_LABEL = {
  operating_expense: "Operating Expense",
  direct_order_cost: "Direct Order Cost",
  supplier_payment:  "Supplier Payment",
  fabricator_payment:"Fabricator Payment",
  customer_refund:   "Customer Refund",
  owner_drawings:    "Owner Withdrawal",
  capital_movement:  "Capital Movement",
  other:             "Other",
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const dfmt = (s) => s ? new Date(s).toLocaleDateString("en-IN") : "—";
const todayISO = () => istToday();


export default function AccountsPage() {
  const { user } = useAuth();
  const isMainAdmin = user?.role === "admin";
  const [tab, setTab] = useState("dashboard");
  const [accounts, setAccounts] = useState([]);
  const [modes, setModes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [onboarding, setOnboarding] = useState({ unmapped: [], already_linked: [] });

  const loadAll = async () => {
    try {
      const [{ data: fa }, { data: m }, { data: cats }, { data: onb }] = await Promise.all([
        api.get("/accounts/financial-accounts"),
        api.get("/settings/payment-modes"),
        api.get("/accounts/expense-categories"),
        api.get("/accounts/onboarding/candidates"),
      ]);
      setAccounts(fa || []);
      setModes(m || []);
      setCategories(cats || []);
      setOnboarding(onb || { unmapped: [], already_linked: [] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { loadAll(); }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6" data-testid="accounts-page">
      <div className="flex items-center gap-3 mb-4">
        <Wallet size={26} weight="fill" className="text-cyan-300" />
        <h1 className="font-heading text-2xl tracking-widest">ACCOUNTS</h1>
      </div>

      <div className="flex gap-1 mb-4 overflow-x-auto pb-1" data-testid="accounts-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              "px-3 py-2 rounded-sm text-xs font-mono-num tracking-widest whitespace-nowrap " +
              (tab === t.key
                ? "bg-cyan-500 text-white"
                : "bg-slate-800/40 text-slate-400 hover:bg-slate-800")
            }
            data-testid={`tab-${t.key}`}
          >
            {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardTab accounts={accounts} onRefresh={loadAll} />}
      {tab === "money_in"  && <MoneyInTab />}
      {tab === "money_out" && <MoneyOutTab accounts={accounts} modes={modes} categories={categories} onRefresh={loadAll} />}
      {tab === "cash_bank" && <CashBankTab accounts={accounts} modes={modes} onboarding={onboarding} onRefresh={loadAll} />}
      {tab === "transfers" && <TransfersTab accounts={accounts} onRefresh={loadAll} />}
      {tab === "suppliers" && <SuppliersTab accounts={accounts} />}
      {tab === "purchases" && isMainAdmin && <SheetPurchasePanel accounts={accounts} />}
      {tab === "purchases" && <PurchasesTab accounts={accounts} />}
      {tab === "returns" && isMainAdmin && <SheetReturnsList />}
      {tab === "returns" && <PurchaseReturnsTab />}
      {tab === "categories" && <CategoriesTab categories={categories} onRefresh={loadAll} />}
      {tab === "reversals" && <ReversalsTab />}
      {tab === "reversals" && <AuditLogSection />}
    </div>
  );
}


// ---------------- DASHBOARD ----------------
function DashboardTab({ accounts, onRefresh }) {
  const [summary, setSummary] = useState(null);
  const [wd, setWd] = useState(null);
  const [range, setRange] = useState("this_month");
  const [showDrawings, setShowDrawings] = useState(false);
  const [drawings, setDrawings] = useState(null);
  useLive(() => { load(); });
  const load = async () => {
    try {
      const { data } = await api.get("/accounts/summary");
      setSummary(data);
      // where-did-my-money-go
      const from = todayISO().slice(0, 8) + "01";
      const to = todayISO();
      const wdResp = await api.get("/accounts/where-did-my-money-go", { params: { from, to } });
      setWd(wdResp.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);
  if (!summary) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="accounts-dashboard">
      <AccountsHealthCard />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="CASH BALANCE" value={fmt(summary.total_cash_balance)} tone="cyan" testid="dash-cash" />
        <Card label="BANK BALANCE" value={fmt(summary.total_bank_balance)} tone="cyan" testid="dash-bank" />
        <Card label="TOTAL CASH + BANK" value={fmt(summary.total_cash_and_bank)} tone="emerald" testid="dash-total" big />
        <Card label="TODAY MONEY IN" value={fmt(summary.today_money_in)} tone="emerald" testid="dash-today-in" />
        <Card label="TODAY MONEY OUT" value={fmt(summary.today_money_out)} tone="red" testid="dash-today-out" />
        <Card label="THIS MONTH OPERATING EXP." value={fmt(summary.this_month_operating_expenses)} tone="red" testid="dash-month-op" />
        <Card label="THIS MONTH OWNER WITHDRAWAL" value={fmt(summary.this_month_owner_drawings)} tone="amber" testid="dash-month-drawings" onClick={async () => {
          setShowDrawings(true);
          try {
            const from = todayISO().slice(0, 8) + "01";
            const to = todayISO();
            const { data } = await api.get("/accounts/owner-withdrawal-breakdown", { params: { from, to } });
            setDrawings(data);
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        }} />
        <Card label="UNMAPPED FAB PAYMENTS" value={fmt(summary.unmapped_fabricator_payments_total)} tone={summary.unmapped_fabricator_payments_total > 0 ? "amber" : "slate"} testid="dash-unmapped" />
        <Card label="UNMAPPED MONEY IN" value={fmt(summary.unmapped_customer_payments_total || 0)} tone={(summary.unmapped_customer_payments_total || 0) > 0 ? "amber" : "slate"} testid="dash-unmapped-in" />
      </div>

      {(summary.unmapped_customer_payment_modes || []).length > 0 && (
        <div className="surface p-4 rounded-sm border-l-4 border-amber-500/60" data-testid="dash-unmapped-in-panel">
          <div className="flex items-center gap-2 mb-1.5">
            <WarningCircle size={16} weight="fill" className="text-amber-300" />
            <div className="font-heading tracking-widest text-amber-300">⚠ UNMAPPED CUSTOMER PAYMENTS</div>
          </div>
          <div className="text-[11px] text-slate-400 mb-2">
            In payment modes ka koi Financial Account link nahi hai — inke through aayi money kisi bhi Cash/Bank balance me nahi ja rahi. <b>Cash &amp; Bank</b> tab → link karo taaki paisa correct account me pahunche.
          </div>
          <div className="space-y-1">
            {(summary.unmapped_customer_payment_modes || []).map((m) => (
              <div key={m.key} className="flex items-center justify-between px-3 py-1.5 bg-slate-800/40 rounded-sm text-sm" data-testid={`dash-unmapped-mode-${m.key}`}>
                <div className="flex items-center gap-2">
                  <span className="text-slate-200">{m.label}</span>
                  <span className="text-[10px] font-mono-num text-slate-500">{m.count} receipt(s)</span>
                </div>
                <span className="font-mono-num font-bold text-amber-300">{fmt(m.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-account book balances (single source of truth) */}
      {(summary.accounts || []).length > 0 && (
        <div className="surface p-4 rounded-sm border-l-4 border-cyan-500/60" data-testid="dash-account-list">
          <div className="font-heading tracking-widest text-lg mb-2">ACCOUNT-WISE BOOK BALANCE</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {(summary.accounts || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between px-3 py-2 bg-slate-800/40 rounded-sm" data-testid={`dash-account-${a.id}`}>
                <div className="min-w-0 flex-1 pr-2">
                  <div className="text-sm text-slate-200 truncate">{a.account_name}</div>
                  <div className="text-[10px] font-mono-num tracking-widest text-slate-500 uppercase">{a.account_type}</div>
                </div>
                <div className={"font-mono-num font-bold text-base " + (a.account_type === "cash" ? "text-emerald-300" : "text-cyan-300")}>
                  {fmt(a.current_balance)}
                </div>
              </div>
            ))}
            <div className="md:col-span-2 flex items-center justify-between px-3 py-2 bg-slate-900 border border-slate-700 rounded-sm mt-1">
              <div className="text-[10px] font-mono-num tracking-widest text-slate-500">TOTAL COMPANY CASH + BANK</div>
              <div className="font-mono-num font-bold text-lg text-emerald-400">{fmt(summary.total_cash_and_bank)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Where did my money go */}
      <div className="surface p-4 rounded-sm border-l-4 border-amber-500/60">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="font-heading text-lg tracking-widest">WHERE DID MY MONEY GO?</div>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400">
            {wd?.from} → {wd?.to} · Total Expense: <span className="text-red-300 font-bold">{fmt(wd?.total_expense)}</span> · Capital Movement: <span className="text-amber-300 font-bold">{fmt(wd?.total_capital_movement)}</span>
          </div>
        </div>
        {wd && wd.buckets.length === 0 ? (
          <div className="text-slate-500 text-sm italic py-4 text-center">Is month koi payment-out entry nahi ki gayi.</div>
        ) : (
          <div className="space-y-1.5">
            {wd?.buckets.map((b) => {
              const isCapital = ["owner_drawings", "capital_movement"].includes(b.transaction_class);
              return (
                <div key={b.category_key} className="flex items-center justify-between px-3 py-2 bg-slate-800/40 rounded-sm text-sm" data-testid={`wd-row-${b.category_key}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-200">{b.label}</span>
                    {isCapital && (
                      <span className="text-[9px] tracking-widest bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-sm">NOT EXPENSE</span>
                    )}
                    <span className="text-[10px] font-mono-num text-slate-500">({b.count})</span>
                  </div>
                  <span className={"font-mono-num font-bold " + (isCapital ? "text-amber-300" : "text-red-300")}>
                    {fmt(b.total)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Owner Withdrawal Breakdown Dialog */}
      <Dialog open={showDrawings} onOpenChange={(o) => { if (!o) { setShowDrawings(false); setDrawings(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <DialogHeader><DialogTitle>Owner Withdrawal — Purpose Breakdown</DialogTitle></DialogHeader>
          {!drawings ? <div className="text-slate-500 py-8 text-center">Loading…</div> : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-slate-400">
                  {drawings.from} → {drawings.to}
                </div>
                <div className="text-lg font-mono-num font-bold text-amber-300" data-testid="owner-breakdown-total">
                  TOTAL: {fmt(drawings.total)}
                </div>
              </div>
              {drawings.buckets.length === 0 ? (
                <div className="text-slate-500 italic text-sm p-4 text-center">Is period me koi owner withdrawal nahi ki.</div>
              ) : (
                <div className="space-y-1.5 mb-4">
                  {drawings.buckets.map((b) => (
                    <div key={b.purpose} className="flex items-center justify-between px-3 py-2 bg-slate-800/40 rounded-sm text-sm" data-testid={`owner-purpose-${b.purpose.replace(/\s+/g, '_')}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-200">{b.purpose}</span>
                        <span className="text-[10px] font-mono-num text-slate-500">({b.count})</span>
                      </div>
                      <span className="font-mono-num font-bold text-amber-300">{fmt(b.total)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-1">RECENT WITHDRAWALS</div>
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] font-mono-num tracking-widest text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1">DATE</th>
                      <th className="text-left px-2 py-1">PURPOSE</th>
                      <th className="text-right px-2 py-1">AMOUNT</th>
                      <th className="text-left px-2 py-1">PAID TO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drawings.rows.map((r) => (
                      <tr key={r.id} className="border-t border-slate-800">
                        <td className="px-2 py-1.5 font-mono-num text-slate-400">{dfmt(r.date)}</td>
                        <td className="px-2 py-1.5 text-slate-200">{r.purpose}</td>
                        <td className="px-2 py-1.5 text-right font-mono-num font-bold text-amber-300">{fmt(r.amount)}</td>
                        <td className="px-2 py-1.5 text-slate-400">{r.paid_to}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Card({ label, value, tone, big, testid, onClick }) {
  const toneCls = {
    cyan: "text-cyan-300", emerald: "text-emerald-400",
    red: "text-red-400", amber: "text-amber-300", slate: "text-slate-300",
  }[tone] || "text-slate-100";
  return (
    <div
      onClick={onClick}
      className={"bg-slate-800/40 border border-slate-700 p-3 rounded-sm " + (big ? "md:col-span-2 " : "") + (onClick ? "cursor-pointer hover:border-cyan-500/40" : "")}
      data-testid={testid}
    >
      <div className="text-[10px] font-mono-num tracking-widest text-slate-500 flex items-center gap-1">
        {label}{onClick && <span className="text-[9px] text-slate-600">▸ CLICK</span>}
      </div>
      <div className={"font-mono-num font-bold mt-1 " + toneCls + " " + (big ? "text-2xl" : "text-lg")}>{value}</div>
    </div>
  );
}


// ---------------- MONEY IN ----------------
function MoneyInTab() {
  const [rows, setRows] = useState([]);
  useLive(() => { load(); });
  const load = async () => {
    try {
      const { data } = await api.get("/reports/payments");
      setRows(data.grouped || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="surface p-3 rounded-sm border-l-4 border-emerald-500/60" data-testid="money-in-tab">
      <div className="text-[10px] font-mono-num tracking-widest text-emerald-300 mb-2">// MONEY IN — one row per customer receipt</div>
      <table className="w-full text-sm">
        <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
          <tr>
            <th className="text-left px-3 py-2">DATE</th>
            <th className="text-left px-3 py-2">CUSTOMER</th>
            <th className="text-left px-3 py-2">MODE</th>
            <th className="text-right px-3 py-2">AMOUNT</th>
            <th className="text-left px-3 py-2">RECEIVED BY</th>
            <th className="text-right px-3 py-2">ORDERS</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500 italic">Koi payment nahi mila.</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-800" data-testid={`money-in-row-${i}`}>
              <td className="px-3 py-2 font-mono-num text-slate-400 text-xs">{dfmt(r.date)}</td>
              <td className="px-3 py-2 text-slate-200">{r.customer_name}</td>
              <td className="px-3 py-2 font-mono-num text-xs text-cyan-300">{(r.mode || "").toUpperCase()}</td>
              <td className="px-3 py-2 text-right font-mono-num font-bold text-emerald-400">{fmt(r.total_amount)}</td>
              <td className="px-3 py-2 text-slate-400 text-xs">{r.recorded_by_name || r.recorded_by || "—"}</td>
              <td className="px-3 py-2 text-right font-mono-num text-slate-400 text-xs">{r.order_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ---------------- MONEY OUT ----------------
function MoneyOutTab({ accounts, modes, categories, onRefresh }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({
    date: todayISO(), amount: "", paid_to: "", category_key: "",
    subcategory: "", financial_account_id: "", payment_method: "",
    note: "", linked_order_id: "", linked_fabricator_id: "",
    withdrawal_purpose: "", custom_purpose: "",
  });
  const [busy, setBusy] = useState(false);
  useLive(() => { load(); });
  const load = async () => {
    try {
      const { data } = await api.get("/accounts/payment-out");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const selectedCat = categories.find((c) => c.key === f.category_key);
  const txnClass = selectedCat?.transaction_class || "other";
  const isOwnerWithdrawal = txnClass === "owner_drawings";
  const hasNoAccounts = accounts.length === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!(Number(f.amount) > 0)) return toast.error("Amount daaliye");
    if (!f.paid_to.trim()) return toast.error("Paid to bataye");
    if (!f.category_key) return toast.error("Category chuno");
    if (!f.financial_account_id) return toast.error("Paid From Account chuno");
    // Resolve owner-withdrawal purpose (custom > selected)
    let purpose = null;
    if (isOwnerWithdrawal) {
      purpose = (f.custom_purpose || "").trim() || f.withdrawal_purpose || "";
      if (!purpose) return toast.error("Withdrawal Purpose bataye");
    }
    setBusy(true);
    try {
      await api.post("/accounts/payment-out", {
        ...f, amount: Number(f.amount),
        transaction_class: txnClass,
        withdrawal_purpose: purpose,
      });
      toast.success("Payment out saved");
      setShowForm(false);
      setF({...f, amount: "", paid_to: "", note: "", subcategory: "", withdrawal_purpose: "", custom_purpose: ""});
      load(); onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reverse = async (id) => {
    const reason = window.prompt("Reversal ka reason?");
    if (!reason?.trim()) return;
    try {
      await api.post(`/accounts/payment-out/${id}/reverse`, { reason });
      toast.success("Reversed");
      load(); onRefresh?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="money-out-tab">
      {hasNoAccounts && (
        <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/40 rounded-sm text-amber-200 text-xs" data-testid="mo-no-accounts-banner">
          <WarningCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-heading tracking-widest text-amber-300 mb-0.5">FINANCIAL ACCOUNT NAHI HAI</div>
            <div>Payment out record karne ke liye pehle ek Financial Account banao (Cash, AXIS, IndusInd, UCO, etc.).<br/>
            <b>Cash &amp; Bank</b> tab → <b>+ ADD ACCOUNT</b> click karo.</div>
          </div>
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} disabled={hasNoAccounts} className="btn-industrial rounded-sm h-10 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="money-out-add">
          <Plus size={14} className="mr-1" /> ADD PAYMENT OUT
        </Button>
      </div>
      <div className="surface p-3 rounded-sm border-l-4 border-red-500/60">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
            <tr>
              <th className="text-left px-3 py-2">DATE</th>
              <th className="text-left px-3 py-2">PAID TO</th>
              <th className="text-left px-3 py-2">CATEGORY</th>
              <th className="text-left px-3 py-2">CLASS</th>
              <th className="text-right px-3 py-2">AMOUNT</th>
              <th className="text-left px-3 py-2">ACCOUNT</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500 italic">Koi payment-out nahi mila. + ADD PAYMENT OUT click karo.</td></tr>}
            {rows.map((r) => {
              const cat = categories.find((c) => c.key === r.category_key);
              const acc = accounts.find((a) => a.id === r.financial_account_id);
              const isRev = !!r.reversal_of;
              const wasReversed = !!r.reversed_at;
              return (
                <tr key={r.id} className={"border-t border-slate-800 " + (wasReversed ? "opacity-60" : "")} data-testid={`money-out-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono-num text-slate-400 text-xs">{dfmt(r.date)}</td>
                  <td className="px-3 py-2 text-slate-200">
                    {r.paid_to}
                    {isRev && <span className="ml-1.5 text-[9px] tracking-widest bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-sm">REVERSAL</span>}
                    {wasReversed && <span className="ml-1.5 text-[9px] tracking-widest bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-sm">REVERSED</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-300 text-xs">
                    {cat?.label || r.category_key}
                    {r.withdrawal_purpose && (
                      <div className="text-[10px] tracking-widest text-amber-300 font-mono-num mt-0.5" data-testid={`money-out-purpose-${r.id}`}>
                        → {r.withdrawal_purpose}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className={"font-mono-num tracking-widest " + (r.transaction_class === "owner_drawings" ? "text-amber-300" : "text-slate-400")}>
                      {CLASS_LABEL[r.transaction_class] || r.transaction_class}
                    </span>
                  </td>
                  <td className={"px-3 py-2 text-right font-mono-num font-bold " + (isRev ? "text-emerald-400" : "text-red-400")}>
                    {isRev ? "+" : "−"}{fmt(r.amount)}
                  </td>
                  <td className="px-3 py-2 text-slate-400 text-xs">{acc?.account_name || "—"}</td>
                  <td className="px-2 py-2 text-right">
                    {!isRev && !wasReversed && (
                      <button onClick={() => reverse(r.id)} className="text-slate-500 hover:text-amber-300 p-1" title="Reverse this" data-testid={`money-out-reverse-${r.id}`}>
                        <ArrowClockwise size={12} weight="bold" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl" data-testid="money-out-form">
          <DialogHeader><DialogTitle>Add Payment Out</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Date"><input type="date" value={f.date} onChange={(e) => setF({...f, date: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="mo-date" /></Field>
            <Field label="Amount (₹) *"><Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({...f, amount: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="mo-amount" /></Field>
            <Field label="Paid To *"><Input value={f.paid_to} onChange={(e) => setF({...f, paid_to: e.target.value})} placeholder="Ravi (worker) / MSEB / …" className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="mo-paid-to" /></Field>
            <Field label="Category *">
              <Select value={f.category_key} onValueChange={(v) => setF({...f, category_key: v})}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="mo-category"><SelectValue placeholder="Choose category…" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white max-h-80">
                  {categories.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label} <span className="text-slate-500 text-[10px] ml-1">({CLASS_LABEL[c.transaction_class]})</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Paid From Account *">
              <Select value={f.financial_account_id} onValueChange={(v) => setF({...f, financial_account_id: v})}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="mo-account"><SelectValue placeholder="Choose account…" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.account_name} ({a.account_type})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Payment Method (how paid)">
              <Select value={f.payment_method} onValueChange={(v) => setF({...f, payment_method: v})}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="mo-method"><SelectValue placeholder="Cash / UPI / Cheque…" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {PAYMENT_METHODS.map((pm) => <SelectItem key={pm} value={pm}>{pm}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="md:col-span-2">
              <Label className="text-slate-400 text-[10px] font-mono-num tracking-widest">NOTE (optional)</Label>
              <Input value={f.note} onChange={(e) => setF({...f, note: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full mt-1" data-testid="mo-note" />
            </div>
            {isOwnerWithdrawal && (
              <div className="md:col-span-2 flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/40 rounded-sm text-amber-200 text-xs" data-testid="mo-owner-warning">
                <WarningCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                <span>Ye Owner Withdrawal hai — Cash/Bank kam hoga, lekin ye <b>operating profit</b> me count nahi hoga.</span>
              </div>
            )}
            {isOwnerWithdrawal && (
              <>
                <Field label="Withdrawal Purpose *">
                  <Select value={f.withdrawal_purpose} onValueChange={(v) => setF({...f, withdrawal_purpose: v, custom_purpose: ""})}>
                    <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="mo-purpose">
                      <SelectValue placeholder="Choose purpose…" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-600 text-white">
                      {OWNER_PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="+ Add Custom Purpose">
                  <Input
                    value={f.custom_purpose}
                    onChange={(e) => setF({...f, custom_purpose: e.target.value, withdrawal_purpose: ""})}
                    placeholder="Type any new purpose…"
                    className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full"
                    data-testid="mo-custom-purpose"
                  />
                </Field>
              </>
            )}
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="btn-industrial" data-testid="mo-submit">
                {busy ? "SAVING…" : "SAVE PAYMENT OUT"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------- CASH & BANK ----------------
function CashBankTab({ accounts, modes, onboarding, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({account_name: "", account_type: "bank", bank_name: "", account_number_last4: "", ifsc: "", upi_id: "", opening_balance: 0, opening_date: ""});
  const [linkFromMode, setLinkFromMode] = useState(null);   // payment mode being linked
  const [ledgerOf, setLedgerOf] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [showMapper, setShowMapper] = useState(false);
  // Per-account balance summary from `/accounts/summary` (single source of truth)
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/accounts/summary"); setSummary(data); }
      catch (e) { /* silent */ }
    })();
  }, [accounts.length]);

  const openNew = () => { setEditing(null); setLinkFromMode(null); setF({account_name: "", account_type: "bank", bank_name: "", account_number_last4: "", ifsc: "", upi_id: "", opening_balance: 0, opening_date: ""}); setShowForm(true); };
  const openEdit = (a) => { setEditing(a); setLinkFromMode(null); setF({...a}); setShowForm(true); };
  const openLinkFromMode = (m) => {
    // Prefill form from an existing Settings payment mode. On save we
    // create a Financial Account AND patch the mode's mapping so nothing
    // is duplicated.
    setEditing(null);
    setLinkFromMode(m);
    setF({
      account_name: m.suggested_account_name || m.label || m.key,
      account_type: m.suggested_account_type || "bank",
      bank_name: m.bank_name || "",
      account_number_last4: (m.account_number || "").slice(-4),
      ifsc: m.ifsc_code || "",
      upi_id: m.upi_id || "",
      opening_balance: 0,
      opening_date: "",
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {...f, opening_balance: Number(f.opening_balance) || 0};
      let faId;
      if (editing) {
        const { data } = await api.patch(`/accounts/financial-accounts/${editing.id}`, body);
        faId = data.id;
      } else {
        const { data } = await api.post("/accounts/financial-accounts", body);
        faId = data.id;
      }
      if (linkFromMode && faId) {
        await api.patch(`/accounts/payment-modes/${linkFromMode.key}/mapping`, { financial_account_id: faId });
        toast.success(`Account created & linked to ${linkFromMode.label}`);
      } else {
        toast.success("Saved");
      }
      setShowForm(false);
      setLinkFromMode(null);
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openLedger = async (a) => {
    setLedgerOf(a);
    try {
      const { data } = await api.get(`/accounts/financial-accounts/${a.id}/ledger`);
      setLedger(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const balanceFor = (id) => {
    const s = (summary?.accounts || []).find((x) => x.id === id);
    return s?.current_balance ?? null;
  };
  const unmappedModes = onboarding?.unmapped || [];

  return (
    <div className="space-y-3" data-testid="cash-bank-tab">
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setShowMapper(true)} className="rounded-sm h-10" data-testid="open-mapper">
          Payment Mode Mapping
        </Button>
        <Button onClick={openNew} className="btn-industrial rounded-sm h-10" data-testid="add-account">
          <Plus size={14} className="mr-1" /> ADD ACCOUNT
        </Button>
      </div>

      {/* Onboarding — link existing Settings payment methods */}
      {unmappedModes.length > 0 && (
        <div className="surface p-4 rounded-sm border-l-4 border-cyan-500/60" data-testid="onboarding-panel">
          <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="font-heading tracking-widest text-cyan-200">EXISTING PAYMENT ACCOUNTS FOUND</div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                Ye Settings → Payment Methods me pehle se configured hain. Har ek ko real Financial Account se link karo — <b>duplicate mat banao</b>. Multiple modes ek hi FA se link ho sakte hain (jaise SBI UPI + Online SBI → SBI Bank).
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            {unmappedModes.map((m) => (
              <div key={m.key} className="flex items-center justify-between px-3 py-2 bg-slate-800/40 rounded-sm text-sm" data-testid={`onboarding-row-${m.key}`}>
                <div className="min-w-0 flex-1 pr-2">
                  <div className="text-slate-200 truncate">{m.label} <span className="text-[10px] text-slate-500 font-mono-num">· {m.key}</span></div>
                  <div className="text-[10px] tracking-widest text-red-300 font-mono-num">ACCOUNT NOT LINKED</div>
                </div>
                {/* Link to existing FA */}
                {accounts.length > 0 && (
                  <Select onValueChange={async (faId) => {
                    try {
                      await api.patch(`/accounts/payment-modes/${m.key}/mapping`, { financial_account_id: faId });
                      toast.success(`${m.label} → linked`);
                      onRefresh?.();
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                  }}>
                    <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm px-2 w-48 mr-2" data-testid={`onboarding-link-existing-${m.key}`}>
                      <SelectValue placeholder="Link to existing…" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-600 text-white">
                      {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Button onClick={() => openLinkFromMode(m)} className="btn-industrial rounded-sm h-9 text-xs" data-testid={`onboarding-create-${m.key}`}>
                  CREATE &amp; LINK
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {accounts.length === 0 && unmappedModes.length === 0 && (
          <div className="md:col-span-2 p-4 bg-amber-500/10 border border-amber-500/40 rounded-sm text-amber-200 text-sm" data-testid="cb-empty-state">
            <div className="font-heading tracking-widest text-amber-300 mb-1">KOI FINANCIAL ACCOUNT NAHI</div>
            <div className="text-xs mb-2">Apne real accounts add karo — jaise: <b>Cash</b>, <b>Shobhit AXIS</b>, <b>IndusInd Bank</b>, <b>UCO Bank</b>. Upar <b>+ ADD ACCOUNT</b> button use karo.</div>
            <div className="text-[11px] text-amber-300/80">Note: Ye alag concept hai — Payment Mode (jo customer se paise lene ka tarika hai) alag hai, Financial Account (jismein paisa aakhir jama hota hai) alag.</div>
          </div>
        )}
        {accounts.map((a) => (
          <AccountCard key={a.id} a={a} balance={balanceFor(a.id)} summaryRow={(summary?.accounts || []).find((x) => x.id === a.id)} onEdit={() => openEdit(a)} onLedger={() => openLedger(a)} />
        ))}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit" : (linkFromMode ? `Create & Link → ${linkFromMode.label}` : "Add")} Financial Account
            </DialogTitle>
          </DialogHeader>
          {linkFromMode && (
            <div className="text-[11px] tracking-widest text-cyan-300 font-mono-num bg-cyan-500/10 border border-cyan-500/30 rounded-sm p-2" data-testid="link-mode-hint">
              // AFTER SAVE, MODE <b>{linkFromMode.key}</b> WILL BE MAPPED → THIS ACCOUNT
            </div>
          )}
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Account Name *"><Input value={f.account_name} onChange={(e) => setF({...f, account_name: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="fa-name" /></Field>
            <Field label="Type">
              <Select value={f.account_type} onValueChange={(v) => setF({...f, account_type: v})}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="fa-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {["cash","bank","petty_cash","wallet","other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Bank Name"><Input value={f.bank_name} onChange={(e) => setF({...f, bank_name: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" /></Field>
            <Field label="A/C last 4"><Input value={f.account_number_last4} onChange={(e) => setF({...f, account_number_last4: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" /></Field>
            <Field label="IFSC"><Input value={f.ifsc} onChange={(e) => setF({...f, ifsc: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" /></Field>
            <Field label="UPI"><Input value={f.upi_id} onChange={(e) => setF({...f, upi_id: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" /></Field>
            <Field label="Opening Balance"><Input type="number" step="0.01" value={f.opening_balance} onChange={(e) => setF({...f, opening_balance: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="fa-opening" /></Field>
            <Field label="Opening Date"><input type="date" value={(f.opening_date || "").slice(0, 10)} onChange={(e) => setF({...f, opening_date: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="fa-opening-date" /></Field>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" className="btn-industrial" data-testid="fa-submit">SAVE</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ledgerOf} onOpenChange={(o) => { if (!o) { setLedgerOf(null); setLedger(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>{ledgerOf?.account_name} — Ledger</DialogTitle></DialogHeader>
          {ledger ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs mb-3">
                <Metric label="OPENING" value={fmt(ledger.opening_balance)} />
                <Metric label="MONEY IN" value={fmt(ledger.totals.money_in)} tone="emerald" />
                <Metric label="MONEY OUT" value={fmt(ledger.totals.money_out)} tone="red" />
                <Metric label="TRANSFERS ±" value={fmt(ledger.totals.transfer_in - ledger.totals.transfer_out)} />
                <Metric label="CURRENT" value={fmt(ledger.current_balance)} tone="cyan" big />
              </div>
              <table className="w-full text-xs">
                <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
                  <tr>
                    <th className="text-left px-2 py-2">DATE</th>
                    <th className="text-left px-2 py-2">TYPE</th>
                    <th className="text-left px-2 py-2">DESCRIPTION</th>
                    <th className="text-right px-2 py-2">IN</th>
                    <th className="text-right px-2 py-2">OUT</th>
                    <th className="text-right px-2 py-2">BAL</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.events.map((ev, i) => (
                    <tr key={i} className={"border-t border-slate-800 " + (ev.reversed_at ? "opacity-60" : "")}>
                      <td className="px-2 py-1.5 font-mono-num text-slate-400">{dfmt(ev.date)}</td>
                      <td className="px-2 py-1.5 text-slate-300">{ev.type}</td>
                      <td className="px-2 py-1.5 text-slate-400 truncate max-w-xs">{ev.description}</td>
                      <td className="px-2 py-1.5 text-right font-mono-num text-emerald-400 font-bold">{ev.money_in > 0 ? fmt(ev.money_in) : ""}</td>
                      <td className="px-2 py-1.5 text-right font-mono-num text-red-400 font-bold">{ev.money_out > 0 ? fmt(ev.money_out) : ""}</td>
                      <td className="px-2 py-1.5 text-right font-mono-num text-slate-200 font-bold">{fmt(ev.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : <div className="text-slate-500 py-8 text-center">Loading…</div>}
        </DialogContent>
      </Dialog>

      <Dialog open={showMapper} onOpenChange={setShowMapper}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl">
          <DialogHeader><DialogTitle>Payment Method → Money Goes To</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-400 mb-2">
            Har payment mode ko real Financial Account se link karo. <b>Multiple modes ek hi account pe map ho sakte hain</b> — e.g. SBI UPI + Online SBI → SBI Bank. Duplicate accounts mat banao.
          </div>
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 text-[10px] font-mono-num tracking-widest text-slate-500 px-2 py-1">
            <div>PAYMENT METHOD</div>
            <div>→</div>
            <div>MONEY GOES TO</div>
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-auto">
            {modes.map((m) => (
              <ModeMapRow key={m.key} m={m} accounts={accounts} onSaved={onRefresh} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AccountCard({ a, balance, summaryRow, onEdit, onLedger }) {
  const totals = summaryRow ? null : null; // per-account totals come from ledger — kept lean here
  return (
    <div className="bg-slate-800/40 border border-slate-700 rounded-sm p-3 hover:border-cyan-500/40" data-testid={`account-card-${a.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-heading text-cyan-200">{a.account_name}</div>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-500 uppercase">{a.account_type}{a.bank_name ? ` · ${a.bank_name}` : ""}{a.account_number_last4 ? ` · ****${a.account_number_last4}` : ""}</div>
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} className="text-slate-500 hover:text-cyan-300 p-1" data-testid={`account-edit-${a.id}`}><PencilSimple size={12} /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <div className="text-[9px] tracking-widest text-slate-500">OPENING</div>
          <div className="font-mono-num text-sm text-slate-300 font-bold">{fmt(a.opening_balance)}</div>
          <div className="text-[9px] font-mono-num text-slate-500">{a.opening_date ? dfmt(a.opening_date) : "no open date"}</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] tracking-widest text-slate-500">CURRENT</div>
          <div className={"font-mono-num text-base font-bold " + (balance === null ? "text-slate-400" : "text-cyan-300")} data-testid={`account-balance-${a.id}`}>
            {balance === null ? "—" : fmt(balance)}
          </div>
          {a.opening_verified
            ? <div className="text-[9px] tracking-widest bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded-sm inline-block">VERIFIED</div>
            : <div className="text-[9px] tracking-widest bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-sm inline-block" data-testid={`account-not-verified-${a.id}`}>NOT VERIFIED</div>}
        </div>
      </div>
      <button onClick={onLedger} className="w-full h-8 rounded-sm bg-slate-700 hover:bg-slate-600 text-white text-[10px] font-mono-num tracking-widest" data-testid={`account-ledger-${a.id}`}>
        VIEW LEDGER (IN / OUT / TRANSFER)
      </button>
    </div>
  );
}

function ModeMapRow({ m, accounts, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState(m.financial_account_id || "");
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/accounts/payment-modes/${m.key}/mapping`, { financial_account_id: sel || null });
      toast.success("Mapped");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2 bg-slate-800/40 rounded-sm p-2" data-testid={`mode-map-${m.key}`}>
      <div className="flex-1">
        <div className="text-sm text-slate-200">{m.label}</div>
        {!sel && <div className="text-[10px] tracking-widest text-red-300 font-mono-num">ACCOUNT NOT MAPPED</div>}
      </div>
      <Select value={sel} onValueChange={setSel}>
        <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-56" data-testid={`mode-map-select-${m.key}`}><SelectValue placeholder="— none —" /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-600 text-white">
          {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button onClick={save} disabled={saving} className="btn-industrial rounded-sm h-9" data-testid={`mode-map-save-${m.key}`}>SAVE</Button>
    </div>
  );
}


// ---------------- TRANSFERS ----------------
function TransfersTab({ accounts, onRefresh }) {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({from_account_id: "", to_account_id: "", amount: "", date: todayISO(), note: ""});
  useLive(() => { load(); });
  const load = async () => {
    try { const { data } = await api.get("/accounts/transfers"); setRows(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.from_account_id || !f.to_account_id) return toast.error("From/To account select karo");
    if (f.from_account_id === f.to_account_id) return toast.error("Same account nahi ho sakta");
    if (!(Number(f.amount) > 0)) return toast.error("Amount daalo");
    try {
      await api.post("/accounts/transfers", {...f, amount: Number(f.amount)});
      toast.success("Transfer saved");
      setShowForm(false);
      setF({from_account_id: "", to_account_id: "", amount: "", date: todayISO(), note: ""});
      load(); onRefresh?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const nameOf = (id) => accounts.find((a) => a.id === id)?.account_name || "—";
  const hasLessThanTwo = accounts.length < 2;
  return (
    <div className="space-y-3" data-testid="transfers-tab">
      {hasLessThanTwo && (
        <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/40 rounded-sm text-amber-200 text-xs" data-testid="tr-no-accounts-banner">
          <WarningCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-heading tracking-widest text-amber-300 mb-0.5">TRANSFER KE LIYE 2 ACCOUNTS CHAHIYE</div>
            <div>Ek se doosre account me paisa transfer karne ke liye kam se kam 2 Financial Accounts hone chahiye. <b>Cash &amp; Bank</b> tab → <b>+ ADD ACCOUNT</b>.</div>
          </div>
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} disabled={hasLessThanTwo} className="btn-industrial rounded-sm h-10 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="transfer-add">
          <ArrowsLeftRight size={14} className="mr-1" /> TRANSFER MONEY
        </Button>
      </div>
      <div className="surface p-3 rounded-sm">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
            <tr>
              <th className="text-left px-3 py-2">DATE</th>
              <th className="text-left px-3 py-2">FROM</th>
              <th className="text-left px-3 py-2">TO</th>
              <th className="text-right px-3 py-2">AMOUNT</th>
              <th className="text-left px-3 py-2">NOTE</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500 italic">Koi transfer nahi mila.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className={"border-t border-slate-800 " + (r.reversal_of ? "text-slate-500" : "")} data-testid={`transfer-row-${r.id}`}>
                <td className="px-3 py-2 font-mono-num text-slate-400 text-xs">{dfmt(r.date)}</td>
                <td className="px-3 py-2">{nameOf(r.from_account_id)}</td>
                <td className="px-3 py-2">{nameOf(r.to_account_id)}</td>
                <td className="px-3 py-2 text-right font-mono-num font-bold text-cyan-300">{fmt(r.amount)}</td>
                <td className="px-3 py-2 text-slate-400 text-xs">{r.note}{r.reversal_of ? " (REVERSAL)" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle>Transfer Money</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="From Account">
              <Select value={f.from_account_id} onValueChange={(v) => setF({...f, from_account_id: v})}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="tr-from"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="To Account">
              <Select value={f.to_account_id} onValueChange={(v) => setF({...f, to_account_id: v})}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="tr-to"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Amount (₹)"><Input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({...f, amount: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" data-testid="tr-amount" /></Field>
            <Field label="Date"><input type="date" value={f.date} onChange={(e) => setF({...f, date: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" /></Field>
            <div className="md:col-span-2">
              <Label className="text-slate-400 text-[10px] tracking-widest">NOTE</Label>
              <Input value={f.note} onChange={(e) => setF({...f, note: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full mt-1" />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" className="btn-industrial" data-testid="tr-submit">TRANSFER</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------- CATEGORIES ----------------
function CategoriesTab({ categories, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({key: "", label: "", transaction_class: "operating_expense"});
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accounts/expense-categories", f);
      toast.success("Added");
      setShowForm(false); setF({key: "", label: "", transaction_class: "operating_expense"});
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const grouped = useMemo(() => {
    const g = {};
    for (const c of categories) {
      (g[c.transaction_class] = g[c.transaction_class] || []).push(c);
    }
    return g;
  }, [categories]);
  return (
    <div className="space-y-3" data-testid="categories-tab">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="btn-industrial rounded-sm h-10" data-testid="cat-add">
          <Plus size={14} className="mr-1" /> ADD CATEGORY
        </Button>
      </div>
      {Object.entries(grouped).map(([cls, list]) => (
        <div key={cls} className="surface p-3 rounded-sm">
          <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-2">// {CLASS_LABEL[cls] || cls}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            {list.map((c) => (
              <div key={c.key} className="bg-slate-800/40 border border-slate-700 rounded-sm p-2 text-xs" data-testid={`cat-${c.key}`}>
                <div className="text-slate-200 font-medium">{c.label}</div>
                <div className="text-[9px] font-mono-num text-slate-500">{c.key}{c.is_system && " · system"}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle>Add Expense Category</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Key (unique, snake_case)"><Input value={f.key} onChange={(e) => setF({...f, key: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" /></Field>
            <Field label="Label"><Input value={f.label} onChange={(e) => setF({...f, label: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full" /></Field>
            <Field label="Class">
              <Select value={f.transaction_class} onValueChange={(v) => setF({...f, transaction_class: v})}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm px-2 w-full"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {Object.entries(CLASS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" className="btn-industrial">SAVE</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------- REVERSALS ----------------
function ReversalsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/accounts/payment-out");
        setRows((data || []).filter((r) => r.reversal_of || r.reversed_at));
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);
  return (
    <div className="surface p-3 rounded-sm border-l-4 border-amber-500/60" data-testid="reversals-tab">
      <div className="text-[10px] font-mono-num tracking-widest text-amber-300 mb-2">// REVERSAL / AUDIT TRAIL — nothing hard-deleted</div>
      {rows.length === 0 ? (
        <div className="text-slate-500 italic text-sm p-4">Koi reversal nahi hui.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
            <tr>
              <th className="text-left px-2 py-2">TYPE</th>
              <th className="text-left px-2 py-2">PAID TO</th>
              <th className="text-right px-2 py-2">AMOUNT</th>
              <th className="text-left px-2 py-2">REVERSAL OF / BY</th>
              <th className="text-left px-2 py-2">REASON</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-800" data-testid={`rev-row-${r.id}`}>
                <td className="px-2 py-1.5">{r.reversal_of ? "REVERSAL ENTRY" : "ORIGINAL (REVERSED)"}</td>
                <td className="px-2 py-1.5 text-slate-300">{r.paid_to}</td>
                <td className="px-2 py-1.5 text-right font-mono-num font-bold">{fmt(r.amount)}</td>
                <td className="px-2 py-1.5 text-slate-400">{r.reversal_of || r.reversed_by || "—"}</td>
                <td className="px-2 py-1.5 text-slate-400">{r.reversal_reason || (r.reversal_of ? (r.note || "").replace(/^REVERSAL — /, "") : "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AuditLogSection() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const isAdmin = user?.role === "admin";
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/audit-log", { params: { limit: 200 } })
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [isAdmin]);
  if (!isAdmin) return null;
  return (
    <div className="surface p-3 rounded-sm border-l-4 border-cyan-500/60 mt-4" data-testid="audit-log-section">
      <div className="text-[10px] font-mono-num tracking-widest text-cyan-300 mb-2">// SYSTEM AUDIT LOG — WHO CHANGED WHAT, WHEN</div>
      {rows.length === 0 ? (
        <div className="text-slate-500 italic text-sm p-4">Abhi koi entry nahi.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
              <tr>
                <th className="text-left px-2 py-2">TIME</th>
                <th className="text-left px-2 py-2">USER</th>
                <th className="text-left px-2 py-2">ENTITY</th>
                <th className="text-left px-2 py-2">ACTION</th>
                <th className="text-left px-2 py-2">CHANGED / NOTE</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-800" data-testid={`audit-row-${r.id}`}>
                  <td className="px-2 py-1.5 font-mono-num text-slate-400 whitespace-nowrap">{new Date(r.at).toLocaleString("en-IN")}</td>
                  <td className="px-2 py-1.5 text-slate-300">{r.user_name || r.user_email} <span className="text-slate-500">({r.user_role})</span></td>
                  <td className="px-2 py-1.5 text-slate-300">{r.entity}{(r.after?.name || r.before?.name) ? ` · ${r.after?.name || r.before?.name}` : ""}</td>
                  <td className="px-2 py-1.5 font-mono-num text-orange-300">{r.action}</td>
                  <td className="px-2 py-1.5 text-slate-400">{(r.changed_fields || []).join(", ")}{r.note ? ` ${r.note}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- shared bits ----
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-slate-400 text-[10px] font-mono-num tracking-widest">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Metric({ label, value, tone, big }) {
  const toneCls = { emerald: "text-emerald-400", red: "text-red-400", cyan: "text-cyan-300" }[tone] || "text-slate-200";
  return (
    <div className="bg-slate-800/40 border border-slate-700 rounded-sm p-2 text-center">
      <div className="text-[9px] font-mono-num tracking-widest text-slate-500">{label}</div>
      <div className={"font-mono-num font-bold " + toneCls + " " + (big ? "text-lg" : "text-sm")}>{value}</div>
    </div>
  );
}


// ---------------- SUPPLIERS TAB ----------------
function SuppliersTab({ accounts }) {
  const [suppliers, setSuppliers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ name: "", mobile: "", state: "", gstin: "",
    gst_registered: false, opening_payable: 0, opening_date: todayISO(), notes: "", is_active: true });
  const [ledgerOf, setLedgerOf] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [pay, setPay] = useState({ amount: "", financial_account_id: "", date: todayISO(), payment_method: "", note: "" });
  useLive(() => { load(); });
  const load = async () => {
    try { const { data } = await api.get("/suppliers"); setSuppliers(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Supplier name required");
    try {
      await api.post("/suppliers", { ...f, opening_payable: Number(f.opening_payable) || 0 });
      toast.success("Supplier added");
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openLedger = async (s) => {
    setLedgerOf(s);
    try { const { data } = await api.get(`/suppliers/${s.id}/ledger`); setLedger(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitPay = async (e) => {
    e.preventDefault();
    if (!(Number(pay.amount) > 0)) return toast.error("Amount daaliye");
    if (!pay.financial_account_id) return toast.error("Cash/Bank chuno");
    try {
      await api.post(`/suppliers/${ledgerOf.id}/payments`, {
        supplier_id: ledgerOf.id, amount: Number(pay.amount),
        financial_account_id: pay.financial_account_id, date: pay.date,
        payment_method: pay.payment_method || null, note: pay.note,
      });
      toast.success("Payment recorded");
      setShowPay(false); setPay({ amount: "", financial_account_id: "", date: todayISO(), payment_method: "", note: "" });
      openLedger(ledgerOf);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="suppliers-tab">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="btn-industrial rounded-sm h-10" data-testid="supplier-add">
          <Plus size={14} className="mr-1" /> ADD SUPPLIER
        </Button>
      </div>
      <div className="border border-slate-700 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800 text-[10px] font-mono-num tracking-widest text-slate-400">
            <tr><th className="px-2 py-1.5 text-left">SUPPLIER</th><th className="px-2 py-1.5 text-left">MOBILE</th><th className="px-2 py-1.5 text-left">STATE</th><th className="px-2 py-1.5 text-right">OPENING</th><th className="px-2 py-1.5"></th></tr>
          </thead>
          <tbody>
            {suppliers.map((s) => (
              <tr key={s.id} className="border-t border-slate-800" data-testid={`supplier-row-${s.id}`}>
                <td className="px-2 py-2 text-slate-200">{s.name}</td>
                <td className="px-2 py-2 font-mono-num text-slate-400">{s.mobile || "—"}</td>
                <td className="px-2 py-2 text-slate-400">{s.state || "—"}</td>
                <td className="px-2 py-2 text-right font-mono-num text-slate-300">{fmt(s.opening_payable || 0)}</td>
                <td className="px-2 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => openLedger(s)} className="h-8 text-xs rounded-sm" data-testid={`supplier-ledger-${s.id}`}>LEDGER</Button>
                </td>
              </tr>
            ))}
            {suppliers.length === 0 && <tr><td colSpan="5" className="text-center py-6 text-slate-500 italic">Koi supplier nahi. + ADD SUPPLIER click karo.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle>Add Supplier</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <Field label="Name *"><Input value={f.name} onChange={(e) => setF({...f, name: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10" data-testid="supplier-name" /></Field>
            <Field label="Mobile"><Input value={f.mobile} onChange={(e) => setF({...f, mobile: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 font-mono-num" /></Field>
            <Field label="State"><Input value={f.state} onChange={(e) => setF({...f, state: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10" /></Field>
            <Field label="GSTIN"><Input value={f.gstin} onChange={(e) => setF({...f, gstin: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 font-mono-num" /></Field>
            <Field label="Opening Payable"><Input type="number" value={f.opening_payable} onChange={(e) => setF({...f, opening_payable: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 font-mono-num" /></Field>
            <Field label="Opening Date"><Input type="date" value={f.opening_date} onChange={(e) => setF({...f, opening_date: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 font-mono-num" /></Field>
            <div className="col-span-2 flex justify-end gap-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>CANCEL</Button>
              <Button type="submit" className="btn-industrial" data-testid="supplier-save">SAVE</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ledgerOf} onOpenChange={(o) => { if (!o) { setLedgerOf(null); setLedger(null); } }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl">
          <DialogHeader><DialogTitle>{ledgerOf?.name} · Supplier Ledger</DialogTitle></DialogHeader>
          {ledger && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <Metric label="OPENING PAYABLE" value={fmt(ledger.opening_payable)} tone="cyan" />
                <Metric label="TOTAL PURCHASES" value={fmt(ledger.total_purchases)} tone="slate" />
                <Metric label="TOTAL PAID" value={fmt(ledger.total_paid)} tone="emerald" />
                <Metric label={ledger.current_due >= 0 ? "CURRENT DUE" : "ADVANCE"} value={fmt(Math.abs(ledger.current_due))} tone={ledger.current_due > 0 ? "red" : "emerald"} big />
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setShowPay(true)} className="btn-industrial rounded-sm h-9 text-xs" data-testid="supplier-pay-btn"><Plus size={12} className="mr-1"/>PAY SUPPLIER</Button>
              </div>
              <div className="border border-slate-700 rounded-sm overflow-hidden max-h-[50vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800 text-[10px] font-mono-num tracking-widest text-slate-400 sticky top-0">
                    <tr><th className="px-2 py-1.5 text-left">DATE</th><th className="px-2 py-1.5 text-left">TYPE</th><th className="px-2 py-1.5 text-left">REF</th><th className="px-2 py-1.5 text-right">DEBIT</th><th className="px-2 py-1.5 text-right">CREDIT</th><th className="px-2 py-1.5 text-right">BALANCE</th></tr>
                  </thead>
                  <tbody>
                    {ledger.events.map((ev) => (
                      <tr key={ev.id} className="border-t border-slate-800">
                        <td className="px-2 py-1.5 font-mono-num text-[11px] text-slate-500">{(ev.created_at||"").slice(0,10)}</td>
                        <td className="px-2 py-1.5 text-[10px] tracking-widest">{ev.type}</td>
                        <td className="px-2 py-1.5 text-[11px] text-slate-400">{ev.reference_no || "—"}</td>
                        <td className="px-2 py-1.5 text-right font-mono-num text-emerald-300">{ev.debit ? fmt(ev.debit) : ""}</td>
                        <td className="px-2 py-1.5 text-right font-mono-num text-red-300">{ev.credit ? fmt(ev.credit) : ""}</td>
                        <td className="px-2 py-1.5 text-right font-mono-num font-bold">{fmt(ev.balance)}</td>
                      </tr>
                    ))}
                    {ledger.events.length === 0 && <tr><td colSpan="6" className="text-center py-4 text-slate-500 italic">No transactions yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle>Pay Supplier — {ledgerOf?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitPay} className="space-y-3">
            <Field label="Amount *"><Input type="number" value={pay.amount} onChange={(e) => setPay({...pay, amount: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 font-mono-num" data-testid="pay-supp-amount" /></Field>
            <Field label="Paid From Account *">
              <Select value={pay.financial_account_id} onValueChange={(v) => setPay({...pay, financial_account_id: v})}>
                <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10" data-testid="pay-supp-fa"><SelectValue placeholder="Choose account…" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date"><Input type="date" value={pay.date} onChange={(e) => setPay({...pay, date: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 font-mono-num" /></Field>
            <Field label="Note"><Input value={pay.note} onChange={(e) => setPay({...pay, note: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10" /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowPay(false)}>CANCEL</Button>
              <Button type="submit" className="btn-industrial" data-testid="pay-supp-save">RECORD PAYMENT</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------- PURCHASES TAB ----------------
function PurchasesTab({ accounts }) {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [returnOf, setReturnOf] = useState(null); // full purchase doc
  const [f, setF] = useState({
    supplier_id: "", invoice_no: "", invoice_date: todayISO(), notes: "",
    lines: [{ item_id: "", quantity: "", rate_per_unit: "", sheet_count: "", total_kg: "", total_sqft: "", line_amount: "", gst_percent: 18, gst_inclusive: false, itc_eligible: true }],
    payment: { amount: "", financial_account_id: "", payment_method: "", payment_date: todayISO() },
  });
  useLive(() => { load(); });
  const load = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/purchases", { params: { limit: 100 } }),
        api.get("/suppliers"), api.get("/items"),
      ]);
      setRows(r1.data || []); setSuppliers(r2.data || []); setItems(r3.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const addLine = () => setF({...f, lines: [...f.lines, { item_id: "", quantity: "", rate_per_unit: "", sheet_count: "", total_kg: "", total_sqft: "", line_amount: "", gst_percent: 18, gst_inclusive: false, itc_eligible: true }]});
  const setLine = (i, patch) => {
    const ls = [...f.lines]; ls[i] = { ...ls[i], ...patch };
    // Auto-compute line_amount if qty & rate set and line_amount not overridden
    if (patch.quantity !== undefined || patch.rate_per_unit !== undefined) {
      const q = Number(ls[i].quantity || 0), r = Number(ls[i].rate_per_unit || 0);
      if (q > 0 && r > 0 && !patch.line_amount) ls[i].line_amount = (q * r).toFixed(2);
    }
    setF({...f, lines: ls});
  };
  const removeLine = (i) => setF({...f, lines: f.lines.filter((_, j) => j !== i)});
  const invoiceTotal = f.lines.reduce((s, l) => s + Number(l.line_amount || Number(l.quantity||0)*Number(l.rate_per_unit||0)), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.supplier_id) return toast.error("Supplier chuno");
    if (f.lines.some((l) => !l.item_id || !(Number(l.quantity)>0))) return toast.error("Sabhi lines me item aur qty daalo");
    const payload = {
      supplier_id: f.supplier_id, invoice_no: f.invoice_no, invoice_date: f.invoice_date, notes: f.notes,
      status: "posted",
      lines: f.lines.map((l) => ({
        item_id: l.item_id, quantity: Number(l.quantity), rate_per_unit: Number(l.rate_per_unit) || 0,
        sheet_count: l.sheet_count ? Number(l.sheet_count) : null,
        total_kg: l.total_kg ? Number(l.total_kg) : null,
        total_sqft: l.total_sqft ? Number(l.total_sqft) : null,
        line_amount: l.line_amount ? Number(l.line_amount) : null,
        gst_percent: Number(l.gst_percent) || 0,
        gst_inclusive: !!l.gst_inclusive,
        itc_eligible: l.itc_eligible !== false,
      })),
      payment: Number(f.payment.amount) > 0 ? {
        amount: Number(f.payment.amount),
        financial_account_id: f.payment.financial_account_id,
        payment_method: f.payment.payment_method || null,
        payment_date: f.payment.payment_date,
      } : { amount: 0 },
      idempotency_key: `purchase-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
    try {
      await api.post("/purchases", payload);
      toast.success("Purchase posted");
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="purchases-tab">
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(true)} className="btn-industrial rounded-sm h-10" data-testid="purchase-add">
          <Plus size={14} className="mr-1" /> NEW PURCHASE
        </Button>
      </div>
      <div className="border border-slate-700 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800 text-[10px] font-mono-num tracking-widest text-slate-400">
            <tr><th className="px-2 py-1.5 text-left">DATE</th><th className="px-2 py-1.5 text-left">PO#</th><th className="px-2 py-1.5 text-left">SUPPLIER</th><th className="px-2 py-1.5 text-left">INVOICE#</th><th className="px-2 py-1.5 text-right">AMOUNT</th><th className="px-2 py-1.5 text-right">PAID</th><th className="px-2 py-1.5 text-right">DUE</th><th className="px-2 py-1.5 text-left">STATUS</th><th className="px-2 py-1.5 text-right">ACTION</th></tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-slate-800" data-testid={`purchase-row-${p.id}`}>
                <td className="px-2 py-2 font-mono-num text-[11px] text-slate-500">{(p.purchase_date||p.created_at||"").slice(0,10)}</td>
                <td className="px-2 py-2 font-mono-num text-cyan-300">{p.purchase_no || "—"}</td>
                <td className="px-2 py-2 text-slate-200">{p.supplier_name || p.supplier || "—"}</td>
                <td className="px-2 py-2 font-mono-num text-slate-400">{p.invoice_no || "—"}</td>
                <td className="px-2 py-2 text-right font-mono-num">{fmt(p.total_amount || p.totals?.invoice_total || 0)}</td>
                <td className="px-2 py-2 text-right font-mono-num text-emerald-300">{fmt(p.payment?.amount_paid || 0)}</td>
                <td className="px-2 py-2 text-right font-mono-num text-red-300">{fmt(p.supplier_due || 0)}</td>
                <td className="px-2 py-2 text-[10px] tracking-widest">{(p.status || "posted").toUpperCase()}</td>
                <td className="px-2 py-2 text-right">
                  {(p.status || "posted").toLowerCase() === "posted" && (
                    <Button size="sm" variant="outline"
                      onClick={() => setReturnOf(p)}
                      className="h-7 text-[10px] tracking-widest rounded-sm border-amber-500/60 text-amber-300 hover:bg-amber-500/10"
                      data-testid={`purchase-return-btn-${p.id}`}>
                      RETURN
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-6 text-slate-500 italic">No purchases yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {returnOf && (
        <PurchaseReturnDialog
          purchase={returnOf}
          accounts={accounts}
          onClose={() => setReturnOf(null)}
          onDone={() => { setReturnOf(null); load(); }}
        />
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Purchase</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Supplier *">
                <Select value={f.supplier_id} onValueChange={(v) => setF({...f, supplier_id: v})}>
                  <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10" data-testid="pur-supplier"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-600 text-white">
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Invoice No"><Input value={f.invoice_no} onChange={(e) => setF({...f, invoice_no: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 font-mono-num" data-testid="pur-invoice" /></Field>
              <Field label="Invoice Date"><Input type="date" value={f.invoice_date} onChange={(e) => setF({...f, invoice_date: e.target.value})} className="bg-slate-800/60 border-slate-600 text-white h-10 font-mono-num" /></Field>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-mono-num tracking-widest text-slate-400">LINES</div>
                <Button type="button" onClick={addLine} size="sm" variant="outline" className="h-8 text-xs">+ ADD LINE</Button>
              </div>
              {f.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-end bg-slate-800/30 p-2 rounded-sm" data-testid={`pur-line-${i}`}>
                  <div className="col-span-3">
                    <Label className="text-[9px] tracking-widest text-slate-500">ITEM</Label>
                    <ItemPicker value={l.item_id} onChange={(v) => setLine(i, { item_id: v })}
                      items={orderPickerItems(items, l.item_id, { service: false })} placeholder="Item…" testid={`pur-line-item-${i}`} />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[9px] tracking-widest text-slate-500">QTY</Label>
                    <Input value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="bg-slate-800/60 border-slate-600 text-white h-9 text-xs font-mono-num" />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[9px] tracking-widest text-slate-500">SHEETS</Label>
                    <Input value={l.sheet_count} onChange={(e) => setLine(i, { sheet_count: e.target.value })} className="bg-slate-800/60 border-slate-600 text-white h-9 text-xs font-mono-num" />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[9px] tracking-widest text-slate-500">KG</Label>
                    <Input value={l.total_kg} onChange={(e) => setLine(i, { total_kg: e.target.value })} className="bg-slate-800/60 border-slate-600 text-white h-9 text-xs font-mono-num" />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[9px] tracking-widest text-slate-500">SQFT</Label>
                    <Input value={l.total_sqft} onChange={(e) => setLine(i, { total_sqft: e.target.value })} className="bg-slate-800/60 border-slate-600 text-white h-9 text-xs font-mono-num" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[9px] tracking-widest text-slate-500">RATE/UNIT</Label>
                    <Input value={l.rate_per_unit} onChange={(e) => setLine(i, { rate_per_unit: e.target.value })} className="bg-slate-800/60 border-slate-600 text-white h-9 text-xs font-mono-num" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[9px] tracking-widest text-slate-500">LINE ₹</Label>
                    <Input value={l.line_amount} onChange={(e) => setLine(i, { line_amount: e.target.value })} className="bg-slate-800/60 border-slate-600 text-white h-9 text-xs font-mono-num" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {f.lines.length > 1 && <button type="button" onClick={() => removeLine(i)} className="text-red-400 hover:text-red-300"><Trash size={14} /></button>}
                  </div>
                  <div className="col-span-12 grid grid-cols-6 gap-1.5 mt-1 pt-1 border-t border-slate-800">
                    <div>
                      <Label className="text-[9px] tracking-widest text-slate-500">GST %</Label>
                      <Input value={l.gst_percent} onChange={(e) => setLine(i, { gst_percent: e.target.value })} className="bg-slate-800/60 border-slate-600 text-white h-8 text-xs font-mono-num" data-testid={`pur-line-gst-${i}`} />
                    </div>
                    <label className="flex items-center gap-1.5 text-[10px] tracking-widest text-slate-400 cursor-pointer pt-4">
                      <input type="checkbox" checked={!!l.gst_inclusive} onChange={(e) => setLine(i, { gst_inclusive: e.target.checked })} data-testid={`pur-line-incl-${i}`} />
                      RATE INCL. GST
                    </label>
                    <label className="flex items-center gap-1.5 text-[10px] tracking-widest text-slate-400 cursor-pointer pt-4">
                      <input type="checkbox" checked={l.itc_eligible !== false} onChange={(e) => setLine(i, { itc_eligible: e.target.checked })} data-testid={`pur-line-itc-${i}`} />
                      ITC ELIGIBLE
                    </label>
                  </div>
                </div>
              ))}
              <div className="text-right text-sm font-mono-num text-cyan-300">INVOICE TOTAL: <b>{fmt(invoiceTotal)}</b></div>
            </div>
            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-800">
              <Field label="Pay Now">
                <Input type="number" value={f.payment.amount} onChange={(e) => setF({...f, payment: {...f.payment, amount: e.target.value}})} className="bg-slate-800/60 border-slate-600 text-white h-10 font-mono-num" data-testid="pur-pay-amount" placeholder="0 = credit" />
              </Field>
              <Field label="From Account">
                <Select value={f.payment.financial_account_id} onValueChange={(v) => setF({...f, payment: {...f.payment, financial_account_id: v}})}>
                  <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10" data-testid="pur-pay-fa"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-600 text-white">
                    {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Payment Method">
                <Select value={f.payment.payment_method} onValueChange={(v) => setF({...f, payment: {...f.payment, payment_method: v}})}>
                  <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-600 text-white">
                    {["Cash","UPI","Bank Transfer","NEFT / RTGS","Cheque","Card","Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>CANCEL</Button>
              <Button type="submit" className="btn-industrial" data-testid="pur-save">POST PURCHASE</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================
// PURCHASE RETURN DIALOG — line-selector + refund options
// ============================================================
function PurchaseReturnDialog({ purchase, accounts, onClose, onDone }) {
  const [linesStatus, setLinesStatus] = useState([]);
  const [selected, setSelected] = useState({});   // { origIdx: qty }
  const [reason, setReason] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [returnDate, setReturnDate] = useState(todayISO());
  const [refundType, setRefundType] = useState("adjust_supplier_due");
  const [refundFA, setRefundFA] = useState("");
  const [refundReceived, setRefundReceived] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/purchases/${purchase.id}/returns`);
        setLinesStatus(data.lines_status || []);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [purchase.id]);

  const setQty = (idx, v) => setSelected({ ...selected, [idx]: v });

  const preview = useMemo(() => {
    let total = 0, gstEst = 0;
    for (const ls of linesStatus) {
      const q = Number(selected[ls.original_line_index] || 0);
      if (q <= 0) continue;
      const gross = ls.gst_inclusive
        ? Number(ls.rate_per_unit || 0)
        : Number(ls.rate_per_unit || 0) * (1 + Number(ls.gst_percent || 0) / 100);
      const taxable = ls.gst_inclusive
        ? Number(ls.rate_per_unit || 0) / (1 + Number(ls.gst_percent || 0) / 100)
        : Number(ls.rate_per_unit || 0);
      total += q * gross;
      gstEst += q * (gross - taxable);
    }
    return { total, gstEst };
  }, [selected, linesStatus]);

  const submit = async (e) => {
    e.preventDefault();
    const lines = Object.entries(selected)
      .filter(([, q]) => Number(q) > 0)
      .map(([idx, q]) => ({ original_line_index: Number(idx), quantity: Number(q) }));
    if (lines.length === 0) return toast.error("At least ek line ka qty daalo");
    if (refundType === "cash_refund" && refundReceived && !refundFA)
      return toast.error("Cash refund ke liye account chuno");
    setSaving(true);
    try {
      await api.post(`/purchases/${purchase.id}/return`, {
        purchase_id: purchase.id,
        return_date: returnDate,
        reason,
        credit_note_no: creditNote,
        refund_type: refundType,
        refund_financial_account_id: refundType === "cash_refund" ? refundFA : null,
        refund_received: refundType === "cash_refund" ? refundReceived : false,
        lines,
        idempotency_key: `ret-${purchase.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      });
      toast.success("Purchase Return posted");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="pr-dialog">
        <DialogHeader>
          <DialogTitle>Purchase Return — {purchase.purchase_no}</DialogTitle>
          <div className="text-xs text-slate-400 mt-1 font-mono-num">
            Supplier: <span className="text-slate-200">{purchase.supplier_name || "—"}</span>
            {" · "}Invoice #{purchase.invoice_no || "—"}
            {" · "}Amount ₹{fmt(purchase.total_amount || purchase.totals?.invoice_total || 0)}
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Return Date">
              <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-9 font-mono-num" data-testid="pr-date" />
            </Field>
            <Field label="Credit Note #">
              <Input value={creditNote} onChange={(e) => setCreditNote(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-9 font-mono-num" placeholder="Optional" data-testid="pr-cn" />
            </Field>
            <Field label="Reason">
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-9" placeholder="e.g. Damaged / wrong material" data-testid="pr-reason" />
            </Field>
          </div>

          <div className="border border-slate-700 rounded-sm">
            <div className="bg-slate-800 px-2 py-1.5 text-[10px] font-mono-num tracking-widest text-slate-400">RETURN LINES</div>
            <table className="w-full text-xs">
              <thead className="bg-slate-800/40 text-[10px] font-mono-num tracking-widest text-slate-500">
                <tr>
                  <th className="px-2 py-1.5 text-left">ITEM</th>
                  <th className="px-2 py-1.5 text-right">ORIG QTY</th>
                  <th className="px-2 py-1.5 text-right">RETURNED</th>
                  <th className="px-2 py-1.5 text-right">REMAINING</th>
                  <th className="px-2 py-1.5 text-right">RATE</th>
                  <th className="px-2 py-1.5 text-right">GST%</th>
                  <th className="px-2 py-1.5 text-right w-28">RETURN QTY</th>
                </tr>
              </thead>
              <tbody>
                {linesStatus.map((ls) => (
                  <tr key={ls.original_line_index} className="border-t border-slate-800" data-testid={`pr-line-${ls.original_line_index}`}>
                    <td className="px-2 py-1.5 text-slate-200">{ls.item_name || "—"} <span className="text-slate-500 text-[10px]">({ls.unit})</span></td>
                    <td className="px-2 py-1.5 text-right font-mono-num text-slate-300">{ls.original_quantity}</td>
                    <td className="px-2 py-1.5 text-right font-mono-num text-amber-300">{ls.already_returned}</td>
                    <td className="px-2 py-1.5 text-right font-mono-num text-cyan-300">{ls.remaining_returnable}</td>
                    <td className="px-2 py-1.5 text-right font-mono-num text-slate-400">₹{fmt(ls.rate_per_unit)}</td>
                    <td className="px-2 py-1.5 text-right font-mono-num text-slate-400">{ls.gst_percent}%{ls.gst_inclusive ? " inc" : ""}</td>
                    <td className="px-2 py-1.5 text-right">
                      <Input value={selected[ls.original_line_index] || ""}
                        onChange={(e) => setQty(ls.original_line_index, e.target.value)}
                        disabled={ls.remaining_returnable <= 0}
                        className="bg-slate-800/60 border-slate-600 text-white h-8 text-xs font-mono-num text-right"
                        placeholder="0"
                        data-testid={`pr-qty-${ls.original_line_index}`}
                      />
                    </td>
                  </tr>
                ))}
                {linesStatus.length === 0 && (
                  <tr><td colSpan="7" className="text-center py-4 text-slate-500 italic">Loading…</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="text-right text-sm font-mono-num text-amber-300">
            RETURN TOTAL (est): <b>₹{fmt(preview.total)}</b>
            <span className="text-slate-500 ml-3">GST reversed: ₹{fmt(preview.gstEst)}</span>
          </div>

          <div className="border-t border-slate-800 pt-3 space-y-2">
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="pr-refund" value="adjust_supplier_due"
                  checked={refundType === "adjust_supplier_due"}
                  onChange={() => setRefundType("adjust_supplier_due")}
                  data-testid="pr-refund-adjust" />
                <span className="text-slate-200">Adjust Supplier Due (Credit Note)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="pr-refund" value="cash_refund"
                  checked={refundType === "cash_refund"}
                  onChange={() => setRefundType("cash_refund")}
                  data-testid="pr-refund-cash" />
                <span className="text-slate-200">Cash / Bank Refund</span>
              </label>
            </div>
            {refundType === "cash_refund" && (
              <div className="grid grid-cols-3 gap-3">
                <Field label="Refund To Account *">
                  <Select value={refundFA} onValueChange={setRefundFA}>
                    <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-9" data-testid="pr-refund-fa"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-600 text-white">
                      {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <label className="flex items-center gap-2 cursor-pointer pt-6 text-sm text-slate-200">
                  <input type="checkbox" checked={refundReceived}
                    onChange={(e) => setRefundReceived(e.target.checked)}
                    data-testid="pr-refund-received" />
                  Refund already received
                </label>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>CANCEL</Button>
            <Button type="submit" className="btn-industrial" disabled={saving} data-testid="pr-save">
              {saving ? "POSTING…" : "POST RETURN"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// PURCHASE RETURNS TAB — list view
// ============================================================
function PurchaseReturnsTab() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  useLive(() => { load(); });
  const load = async () => {
    try {
      const { data } = await api.get("/purchase-returns", { params: { limit: 200 } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const totalReturned = rows.reduce((s, r) => s + Number(r.total_amount || r.totals?.invoice_total || 0), 0);
  const totalGstReversed = rows.reduce((s, r) => s + Number(r.totals?.gst_total || 0), 0);

  return (
    <div className="space-y-3" data-testid="purchase-returns-tab">
      <div className="grid grid-cols-3 gap-3">
        <Metric label="TOTAL RETURNS" value={fmt(totalReturned)} tone="amber" />
        <Metric label="GST REVERSED" value={fmt(totalGstReversed)} tone="cyan" />
        <Metric label="COUNT" value={rows.length} tone="slate" />
      </div>
      <div className="border border-slate-700 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800 text-[10px] font-mono-num tracking-widest text-slate-400">
            <tr>
              <th className="px-2 py-1.5 text-left">DATE</th>
              <th className="px-2 py-1.5 text-left">RETURN #</th>
              <th className="px-2 py-1.5 text-left">ORIG PO#</th>
              <th className="px-2 py-1.5 text-left">SUPPLIER</th>
              <th className="px-2 py-1.5 text-right">AMOUNT</th>
              <th className="px-2 py-1.5 text-right">GST REV.</th>
              <th className="px-2 py-1.5 text-left">MODE</th>
              <th className="px-2 py-1.5 text-left">STATUS</th>
              <th className="px-2 py-1.5 text-right">VIEW</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-800" data-testid={`pr-row-${r.id}`}>
                <td className="px-2 py-2 font-mono-num text-[11px] text-slate-500">{(r.return_date || r.created_at || "").slice(0,10)}</td>
                <td className="px-2 py-2 font-mono-num text-amber-300">{r.return_no || "—"}</td>
                <td className="px-2 py-2 font-mono-num text-cyan-300">{r.purchase_no || "—"}</td>
                <td className="px-2 py-2 text-slate-200">{r.supplier_name || "—"}</td>
                <td className="px-2 py-2 text-right font-mono-num text-red-300">{fmt(r.total_amount || r.totals?.invoice_total || 0)}</td>
                <td className="px-2 py-2 text-right font-mono-num text-slate-300">{fmt(r.totals?.gst_total || 0)}</td>
                <td className="px-2 py-2 text-[10px] tracking-widest text-slate-300">
                  {r.refund_type === "cash_refund"
                    ? (r.refund_received ? "CASH · RECEIVED" : "CASH · PENDING")
                    : "ADJUST DUE"}
                </td>
                <td className="px-2 py-2 text-[10px] tracking-widest">{(r.status || "posted").toUpperCase()}</td>
                <td className="px-2 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => setDetail(r)}
                    className="h-7 text-[10px] tracking-widest rounded-sm"
                    data-testid={`pr-view-${r.id}`}>VIEW</Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="9" className="text-center py-6 text-slate-500 italic">No purchase returns yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>Return {detail.return_no} — {detail.supplier_name}</DialogTitle>
                <div className="text-xs text-slate-400 mt-1">
                  Original: {detail.purchase_no} · Invoice #{detail.invoice_no || "—"} · Reason: {detail.reason || "—"}
                </div>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <Metric label="AMOUNT" value={fmt(detail.total_amount)} tone="amber" />
                  <Metric label="TAXABLE" value={fmt(detail.totals?.taxable_value)} tone="slate" />
                  <Metric label="GST REVERSED" value={fmt(detail.totals?.gst_total)} tone="cyan" />
                  <Metric label="ITC REVERSED" value={fmt(detail.totals?.eligible_itc)} tone="emerald" />
                </div>
                <div className="border border-slate-700 rounded-sm overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-800 text-[10px] font-mono-num tracking-widest text-slate-400">
                      <tr>
                        <th className="px-2 py-1.5 text-left">ITEM</th>
                        <th className="px-2 py-1.5 text-right">QTY</th>
                        <th className="px-2 py-1.5 text-right">RATE</th>
                        <th className="px-2 py-1.5 text-right">TAXABLE</th>
                        <th className="px-2 py-1.5 text-right">GST</th>
                        <th className="px-2 py-1.5 text-right">LINE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.lines || []).map((ln, i) => (
                        <tr key={i} className="border-t border-slate-800">
                          <td className="px-2 py-1.5 text-slate-200">{ln.item_name}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num">{ln.quantity}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num">{fmt(ln.rate_per_unit)}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num">{fmt(ln.taxable_value)}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num">{fmt(ln.gst_total)}</td>
                          <td className="px-2 py-1.5 text-right font-mono-num text-red-300">{fmt(ln.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-slate-500">
                  Refund: <b className="text-slate-300">
                    {detail.refund_type === "cash_refund"
                      ? `Cash/Bank ${detail.refund_received ? "(received)" : "(pending)"}`
                      : "Adjust Supplier Due"}
                  </b>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
